import React, { useState, useEffect } from 'react';
import { Filter, Search, RefreshCw, Calendar, MapPin, AlertTriangle, Check, Eye, ArrowUpDown, ArrowUp, ArrowDown, CheckCircle2, Building2, Plus, XCircle, X, MessageSquare, Sparkles, Info, Image as ImageIcon, ChevronLeft, ChevronRight, Package } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getTasksForOrganization, TaskWithDetails, getTaskCompletionDetails, completeTask, getUserRole } from '@/services/taskService';
import { locationService } from '@/firebase/locationService';
import { useAuth } from '@/contexts/AuthContext';
import { useNotification } from '@/contexts/NotificationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useLocationFilter } from '@/contexts/LocationFilterContext';
import CompleteTaskModal, { TaskCompletionData } from './CompleteTaskModal';
import ViewTaskModal from './ViewTaskModal';
import ReviewTaskModal from './ReviewTaskModal';
import AddTaskPanel from './AddTaskPanel';
import { 
  fetchUserRoles, 
  taskMatchesUserOwnerRoles, 
  normalizeRoles,
  getUserSystemRoleForOrganization,
  getUserOwnerRolesForOrganization,
  isSystemUserInOrganization,
  isAdminInOrganization,
  isManagerInOrganization
} from '@/utils/roleHelpers';
import { usePreview } from '@/contexts/PreviewContext';
import ContextualHelp from './ContextualHelp';
import { normalizeSubscriptionPlan } from '@/firebase/subscriptionService';
import { useTaskCategories } from '@/hooks/useTaskCategories';

const TaskManager: React.FC = () => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { categories: taskCategories } = useTaskCategories();
  const { previewState, isPreviewMode } = usePreview();
  const { subscription } = useSubscription();
  const { showNotification } = useNotification();
  const { selectedLocationIds, hasActiveFilters, availableLocations } = useLocationFilter();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const assetIdFromUrl = searchParams.get('assetId') || null;
  const [tasks, setTasks] = useState<TaskWithDetails[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<TaskWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [assetNameSearch, setAssetNameSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'overdue' | 'completed' | 'pending_review' | 'active'>('active'); // Default to active (pending + overdue)
  const [filterRiskLevel, setFilterRiskLevel] = useState<string>('all');
  const [filterLocation, setFilterLocation] = useState<string>('all');
  const [filterState, setFilterState] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'dueDate' | 'status' | 'location'>('dueDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [hasLoaded, setHasLoaded] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskWithDetails | null>(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showAddTaskPanel, setShowAddTaskPanel] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectedTask, setRejectedTask] = useState<TaskWithDetails | null>(null);
  const [viewingImageUrl, setViewingImageUrl] = useState<string | null>(null);
  const [viewingImageIndex, setViewingImageIndex] = useState<number>(0);
  const [viewingImageUrls, setViewingImageUrls] = useState<string[]>([]);
  const [viewingTask, setViewingTask] = useState<TaskWithDetails | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);

  // Keyboard navigation for image viewer
  useEffect(() => {
    if (!viewingImageUrl) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setViewingImageUrl(null);
        setViewingImageUrls([]);
        setViewingImageIndex(0);
        setViewingTask(null);
      } else if (e.key === 'ArrowLeft' && viewingImageIndex > 0) {
        const newIndex = viewingImageIndex - 1;
        setViewingImageIndex(newIndex);
        setViewingImageUrl(viewingImageUrls[newIndex]);
      } else if (e.key === 'ArrowRight' && viewingImageIndex < viewingImageUrls.length - 1) {
        const newIndex = viewingImageIndex + 1;
        setViewingImageIndex(newIndex);
        setViewingImageUrl(viewingImageUrls[newIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewingImageUrl, viewingImageIndex, viewingImageUrls]);
  const [userOwnerRoles, setUserOwnerRoles] = useState<string[]>([]);
  const [isUserManager, setIsUserManager] = useState<boolean>(false);
  const [isUserAdmin, setIsUserAdmin] = useState<boolean>(false);
  const [isSystemUser, setIsSystemUser] = useState<boolean>(false);
  const [userTaskIds, setUserTaskIds] = useState<Set<string>>(new Set());
  const [filterMyTasks, setFilterMyTasks] = useState<boolean>(false);
  const [allUsers, setAllUsers] = useState<Array<{ id: string; email: string; name?: string; roles?: string[]; role?: string; locationIds?: string[] }>>([]);
  const [taskConversations, setTaskConversations] = useState<Map<string, string>>(new Map()); // taskId -> conversationId
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      if (!hasLoaded && currentOrganization?.id && user?.id) {
        // Load user role and owner roles first, then load tasks (which depends on user role)
        await loadUserRole();
        await loadUserRoles();
        await loadTasks();
        setHasLoaded(true);
      } else if (!hasLoaded && currentOrganization?.id && !user?.id) {
        // No user but have organization - just load tasks
        await loadTasks();
        setHasLoaded(true);
      }
    };
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLoaded, currentOrganization?.id, currentOrganization?.name, user?.id]);

  const loadUserRole = async () => {
    if (user?.id && currentOrganization?.id) {
      try {
        const { db } = await import('@/firebase/config');
        const { doc, getDoc } = await import('firebase/firestore');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const organizationId = currentOrganization.id;
          
          // Get organization-specific system role
          const systemRole = getUserSystemRoleForOrganization(userData, organizationId);
          setUserRole(systemRole || null);
        } else {
          // Fallback to legacy getUserRole
          const role = await getUserRole(user.id);
          setUserRole(role);
        }
      } catch (error) {
        console.error('Error loading user role:', error);
        // Fallback to legacy getUserRole
        const role = await getUserRole(user.id);
        setUserRole(role);
      }
    } else if (user?.id && !currentOrganization?.id) {
      // No organization - use legacy role
      const role = await getUserRole(user.id);
      setUserRole(role);
    }
  };

  const loadUserRoles = async () => {
    if (user?.id && currentOrganization?.id) {
      try {
        const { db } = await import('@/firebase/config');
        const { doc, getDoc } = await import('firebase/firestore');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const organizationId = currentOrganization.id;
          
          // Get organization-specific roles
          const systemRole = getUserSystemRoleForOrganization(userData, organizationId);
          const ownerRoles = getUserOwnerRolesForOrganization(userData, organizationId);
          const isSystemUserRole = isSystemUserInOrganization(userData, organizationId);
          const isUserManagerRole = isManagerInOrganization(userData, organizationId);
          const isUserAdminRole = isAdminInOrganization(userData, organizationId);
          
          setUserOwnerRoles(ownerRoles);
          setIsUserManager(isUserManagerRole);
          setIsUserAdmin(isUserAdminRole);
          setIsSystemUser(isSystemUserRole);
        } else {
          // Fallback to legacy fetchUserRoles
          const roles = await fetchUserRoles(user.id);
          setUserOwnerRoles(roles.ownerRoles);
          setIsUserManager(roles.isSystemManager);
          setIsUserAdmin(roles.systemRoles.includes('admin'));
          setIsSystemUser(roles.isSystemUser);
        }
      } catch (error) {
        console.error('Error loading user roles:', error);
        // Fallback to legacy fetchUserRoles
        const roles = await fetchUserRoles(user.id);
        setUserOwnerRoles(roles.ownerRoles);
        setIsUserManager(roles.isSystemManager);
        setIsUserAdmin(roles.systemRoles.includes('admin'));
        setIsSystemUser(roles.isSystemUser);
      }
    } else if (user?.id && !currentOrganization?.id) {
      // No organization - use legacy roles
      const roles = await fetchUserRoles(user.id);
      setUserOwnerRoles(roles.ownerRoles);
      setIsUserManager(roles.isSystemManager);
      setIsUserAdmin(roles.systemRoles.includes('admin'));
      setIsSystemUser(roles.isSystemUser);
    }
  };

  const isAdminOrManager = () => {
    // Use the state variables that are loaded from organization-specific roles
    return isUserAdmin || isUserManager;
  };

  const loadTaskConversations = async (taskIds: string[]) => {
    if (!currentOrganization?.id || taskIds.length === 0) return;
    
    try {
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const { db } = await import('@/firebase/config');
      
      const conversationsMap = new Map<string, string>();
      
      // Query conversations linked to any of these tasks
      const conversationsRef = collection(db, 'conversations');
      
      // Firestore 'in' queries are limited to 10 items, so we need to batch
      const batchSize = 10;
      for (let i = 0; i < taskIds.length; i += batchSize) {
        const batch = taskIds.slice(i, i + batchSize);
        
        try {
          const conversationsQuery = query(
            conversationsRef,
            where('linkedItems.taskId', 'in', batch)
          );
          const conversationsSnapshot = await getDocs(conversationsQuery);
          
          conversationsSnapshot.forEach((doc) => {
            const data = doc.data();
            const taskId = data.linkedItems?.taskId;
            if (taskId && batch.includes(taskId)) {
              conversationsMap.set(taskId, doc.id);
            }
          });
        } catch (error: any) {
          // If index error, try individual queries as fallback
          if (error.code === 'failed-precondition' || error.message?.includes('index')) {
            console.warn('Batch query failed, trying individual queries:', error);
            for (const taskId of batch) {
              try {
                const taskQuery = query(
                  conversationsRef,
                  where('linkedItems.taskId', '==', taskId)
                );
                const taskSnapshot = await getDocs(taskQuery);
                taskSnapshot.forEach((doc) => {
                  const data = doc.data();
                  if (data.linkedItems?.taskId === taskId) {
                    conversationsMap.set(taskId, doc.id);
                  }
                });
              } catch (err) {
                console.error(`Error loading conversation for task ${taskId}:`, err);
              }
            }
          } else {
            console.error('Error loading task conversations:', error);
          }
        }
      }
      
      setTaskConversations(conversationsMap);
    } catch (error) {
      console.error('Error loading task conversations:', error);
    }
  };

  useEffect(() => {
    applyFilters();
  }, [searchQuery, assetNameSearch, assetIdFromUrl, filterStatus, filterRiskLevel, filterLocation, filterState, filterCategory, sortBy, sortOrder, tasks, filterMyTasks, userTaskIds, selectedLocationIds, hasActiveFilters]);

  const loadTasks = async () => {
    // Don't load tasks if user doesn't have an organization
    if (!currentOrganization?.id) {
      setTasks([]);
      setFilteredTasks([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      // Load all users for the organization to match by owner role
      if (currentOrganization?.id) {
        try {
          const { collection, query, where, getDocs } = await import('firebase/firestore');
          const { db } = await import('@/firebase/config');
          const usersRef = collection(db, 'users');
          
          // Query 1: Users with organizationId matching current organization (legacy support)
          const orgIdQuery = query(usersRef, where('organizationId', '==', currentOrganization.id));
          
          // Query 2: Users with organizationIds array containing current organization
          const orgIdsQuery = query(usersRef, where('organizationIds', 'array-contains', currentOrganization.id));
          
          // Execute both queries in parallel
          const [orgIdSnapshot, orgIdsSnapshot] = await Promise.all([
            getDocs(orgIdQuery),
            getDocs(orgIdsQuery)
          ]);
          
          // Combine results and deduplicate by user ID
          const usersMap = new Map<string, { id: string; email: string; name?: string; roles?: string[]; role?: string; locationIds?: string[] }>();
          
          // Process users from organizationId query
          orgIdSnapshot.forEach((userDoc) => {
            const userData = userDoc.data();
            usersMap.set(userDoc.id, {
              id: userDoc.id,
              email: userData.email || '',
              name: userData.name || userData.displayName || '',
              roles: userData.roles || (userData.role ? [userData.role] : []),
              role: userData.role,
              locationIds: userData.locationIds || []
            });
          });
          
          // Process users from organizationIds array query (may include duplicates)
          orgIdsSnapshot.forEach((userDoc) => {
            // Skip if already added from first query
            if (usersMap.has(userDoc.id)) return;
            
            const userData = userDoc.data();
            usersMap.set(userDoc.id, {
              id: userDoc.id,
              email: userData.email || '',
              name: userData.name || userData.displayName || '',
              roles: userData.roles || (userData.role ? [userData.role] : []),
              role: userData.role,
              locationIds: userData.locationIds || []
            });
          });
          
          // Convert map to array
          const usersList = Array.from(usersMap.values());
          setAllUsers(usersList);
        } catch (error) {
          console.error('Error loading users:', error);
        }
      }

      // Get tasks from the tasks table (already filtered by organization on server side)
      let organizationTasks = await getTasksForOrganization(currentOrganization.id);
      
      // Initialize currentUserOwnerRoles for use later (for "My Tasks" filter)
      let currentUserOwnerRoles: string[] = [];
      
      // In preview mode, use preview user's data if previewing by user, otherwise show all
      // Otherwise, filter by user's accessible locations and roles
      if (isPreviewMode && previewState?.previewType === 'user' && previewState.previewUserId) {
        // Preview as specific user: use their locationIds and roles
        console.log('[TaskManager] Preview mode: previewing as user', previewState.previewUserId);
        
        // Use preview user's locationIds
        if (previewState.locationIds && previewState.locationIds.length > 0) {
          organizationTasks = organizationTasks.filter(task => 
            task.locationId && previewState.locationIds!.includes(task.locationId)
          );
        } else {
          // Preview user has no assigned locations - show no tasks
          organizationTasks = [];
        }
        
        // Fetch preview user's roles to filter by owner_role
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('@/firebase/config');
          const previewUserRef = doc(db, 'users', previewState.previewUserId);
          const previewUserSnap = await getDoc(previewUserRef);
          
          if (previewUserSnap.exists()) {
            const previewUserData = previewUserSnap.data();
            const organizationId = currentOrganization?.id;
            
            let isSystemUserRole = false;
            
            // Use organization-specific roles if organization is available
            if (organizationId) {
              currentUserOwnerRoles = getUserOwnerRolesForOrganization(previewUserData, organizationId);
              isSystemUserRole = isSystemUserInOrganization(previewUserData, organizationId);
            } else {
              // Fallback to global roles if no organization
              const previewUserRoles = normalizeRoles(previewUserData?.roles || previewUserData?.role);
              const SYSTEM_ROLES_LIST = ['user', 'manager', 'admin'];
              currentUserOwnerRoles = previewUserRoles.filter((r: string) => !SYSTEM_ROLES_LIST.includes(r.toLowerCase()));
              isSystemUserRole = previewUserRoles.some((r: string) => r.toLowerCase() === 'user') && 
                                !previewUserRoles.some((r: string) => ['manager', 'admin'].includes(r.toLowerCase()));
            }
            
            // Filter tasks by owner_role for system "User" role users
            if (isSystemUserRole && currentUserOwnerRoles.length > 0) {
              organizationTasks = organizationTasks.filter(task => {
                const taskOwnerRole = task.templateSnapshot?.owner_role || task.ownerRole;
                if (!taskOwnerRole) {
                  return false;
                }
                return taskMatchesUserOwnerRoles(taskOwnerRole, currentUserOwnerRoles);
              });
            }
          }
        } catch (error) {
          console.error('Error fetching preview user roles:', error);
        }
      } else if (!isPreviewMode) {
        // Filter tasks by user's accessible locations (always applies, regardless of system role)
        if (user?.id) {
          // Fetch user's full data from Firestore to get locationIds
          let userLocationIds: string[] | undefined;
          try {
            const { doc, getDoc } = await import('firebase/firestore');
            const { db } = await import('@/firebase/config');
            const userRef = doc(db, 'users', user.id);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const userData = userSnap.data();
              userLocationIds = userData.locationIds;
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
          }
          
          // Filter tasks to only include those at user's assigned locations
          if (userLocationIds && userLocationIds.length > 0) {
            organizationTasks = organizationTasks.filter(task => 
              task.locationId && userLocationIds!.includes(task.locationId)
            );
          } else {
            // User has no assigned locations - show no tasks
            organizationTasks = [];
          }
        }
        
        // Fetch user's roles for the current organization to determine if we need to filter by owner_role
        let isSystemUserRole = false;
        if (user?.id && currentOrganization?.id) {
          try {
            const { doc, getDoc } = await import('firebase/firestore');
            const { db } = await import('@/firebase/config');
            const userRef = doc(db, 'users', user.id);
            const userSnap = await getDoc(userRef);
            
            if (userSnap.exists()) {
              const userData = userSnap.data();
              const organizationId = currentOrganization.id;
              
              // Get organization-specific roles
              const ownerRoles = getUserOwnerRolesForOrganization(userData, organizationId);
              const isSystemUserRoleForOrg = isSystemUserInOrganization(userData, organizationId);
              const isUserManagerForOrg = isManagerInOrganization(userData, organizationId);
              
              currentUserOwnerRoles = ownerRoles;
              isSystemUserRole = isSystemUserRoleForOrg;
              
              // Update state for other uses
              setUserOwnerRoles(ownerRoles);
              setIsUserManager(isUserManagerForOrg);
            } else {
              // Fallback to legacy fetchUserRoles
              const roles = await fetchUserRoles(user.id);
              currentUserOwnerRoles = roles.ownerRoles;
              isSystemUserRole = roles.isSystemUser;
              setUserOwnerRoles(roles.ownerRoles);
              setIsUserManager(roles.isSystemManager);
            }
          } catch (error) {
            console.error('Error fetching user roles in loadTasks:', error);
            // Fallback to state if fetch fails
            currentUserOwnerRoles = userOwnerRoles;
            // Check if user is system user from state
            const userRoles = normalizeRoles(userRole || undefined);
            isSystemUserRole = userRoles.length === 1 && userRoles[0] === 'user' && !isUserManager;
          }
        } else if (user?.id && !currentOrganization?.id) {
          // No organization - use legacy fetchUserRoles
          try {
            const roles = await fetchUserRoles(user.id);
            currentUserOwnerRoles = roles.ownerRoles;
            isSystemUserRole = roles.isSystemUser;
            setUserOwnerRoles(roles.ownerRoles);
            setIsUserManager(roles.isSystemManager);
          } catch (error) {
            console.error('Error fetching user roles in loadTasks:', error);
            currentUserOwnerRoles = userOwnerRoles;
            const userRoles = normalizeRoles(userRole || undefined);
            isSystemUserRole = userRoles.length === 1 && userRoles[0] === 'user' && !isUserManager;
          }
        }
        
        // Filter tasks by owner_role for system "User" role users
        if (isSystemUserRole && currentUserOwnerRoles.length > 0) {
          organizationTasks = organizationTasks.filter(task => {
            const taskOwnerRole = task.templateSnapshot?.owner_role || task.ownerRole;
            if (!taskOwnerRole) {
              // Tasks without owner_role should not be shown to system users
              console.log(`[TaskManager] Task "${task.templateName}" excluded: no owner_role`);
              return false;
            }
            const matches = taskMatchesUserOwnerRoles(taskOwnerRole, currentUserOwnerRoles);
            if (!matches) {
              console.log(`[TaskManager] Task "${task.templateName}" excluded: owner_role="${taskOwnerRole}" doesn't match user roles:`, currentUserOwnerRoles);
            }
            return matches;
          });
        }
      } else {
        // In preview mode: show all tasks for the organization (admin view)
        // Don't filter by locationIds or owner roles
        // For "My Tasks" filter, we'll use empty array (no tasks will match, which is fine for preview)
        console.log('[TaskManager] Preview mode: showing all tasks for organization');
        currentUserOwnerRoles = []; // Empty array so "My Tasks" filter won't match anything in preview mode
      }
      
      // Load completion details for completed tasks
      const tasksWithCompletions = await Promise.all(
        organizationTasks.map(async (task) => {
          if (task.status === 'completed') {
            const completionDetails = await getTaskCompletionDetails(task.id);
            return { ...task, completionDetails };
          }
          return task;
        })
      );
      
      // Identify which tasks are assigned to user based on owner roles
      // For "My Tasks" filter - track which tasks match user's owner roles
      const assignedTaskIds = new Set<string>();
      if (user?.id) {
        // Track tasks matching user's owner roles (for "My Tasks" filter)
        tasksWithCompletions.forEach(task => {
          const taskOwnerRole = task.templateSnapshot?.owner_role || task.ownerRole;
          if (taskMatchesUserOwnerRoles(taskOwnerRole, currentUserOwnerRoles)) {
            assignedTaskIds.add(task.id);
          }
        });
      }
      
      setUserTaskIds(assignedTaskIds);
      
      // Filter out completed tasks from initial display, but keep them in the tasks array
      const nonCompletedTasks = tasksWithCompletions.filter(task => task.status !== 'completed');
      setTasks(tasksWithCompletions);
      setFilteredTasks(nonCompletedTasks);
      
      // Load conversations for all tasks
      const taskIds = tasksWithCompletions.map(t => t.id).filter(Boolean) as string[];
      if (taskIds.length > 0) {
        await loadTaskConversations(taskIds);
      }
    } catch (error) {
      console.error('Error loading tasks:', error);
      showNotification('Failed to load compliance tasks', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...tasks];

    // Apply "My Tasks" filter if enabled
    if (filterMyTasks) {
      filtered = filtered.filter(task => userTaskIds.has(task.id));
    }

    // Filter by status
    if (filterStatus === 'active') {
      // Show tasks that are: overdue, pending review, or due soon (within 30 days)
      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      
      filtered = filtered.filter(task => {
        // Include overdue tasks
        if (task.status === 'overdue') return true;
        
        // Include pending review tasks
        if (task.status === 'pending_review') return true;
        
        // Include pending tasks that are due within 30 days
        if (task.status === 'pending') {
          const dueDate = task.dueDate;
          if (dueDate && dueDate <= thirtyDaysFromNow) {
            return true;
          }
        }
        
        return false;
      });
    } else if (filterStatus === 'pending_review') {
      filtered = filtered.filter(task => task.status === 'pending_review');
    } else if (filterStatus !== 'all') {
      filtered = filtered.filter(task => task.status === filterStatus);
    }

    // Filter by risk level
    if (filterRiskLevel !== 'all') {
      filtered = filtered.filter(task => task.riskLevel === filterRiskLevel);
    }

    // Filter by location - use global filter if active, otherwise use local filter
    if (hasActiveFilters && selectedLocationIds.length > 0) {
      // Use global location filter - filter by locationId
      filtered = filtered.filter(task => task.locationId && selectedLocationIds.includes(task.locationId));
    } else if (filterLocation !== 'all') {
      // Use local location filter - filter by locationName
      filtered = filtered.filter(task => task.locationName === filterLocation);
    }

    // Filter by state
    if (filterState !== 'all') {
      filtered = filtered.filter(task => task.locationState === filterState);
    }

    // Filter by category
    if (filterCategory !== 'all') {
      filtered = filtered.filter(task => (task.category || '').trim() === filterCategory);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(task =>
        task.templateName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.locationName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (task.category || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (task.ownerRole || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by asset (from URL when opened from asset details page)
    if (assetIdFromUrl) {
      filtered = filtered.filter(task =>
        (task.assetIds && Array.isArray(task.assetIds) && task.assetIds.includes(assetIdFromUrl)) ||
        task.checklistContext?.assetId === assetIdFromUrl
      );
    }

    // Filter by asset name (free text search)
    if (assetNameSearch.trim()) {
      const q = assetNameSearch.trim().toLowerCase();
      filtered = filtered.filter(task =>
        (task.assetName || '').toLowerCase().includes(q)
      );
    }

    // Sort tasks
    filtered.sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'dueDate') {
        comparison = a.dueDate.getTime() - b.dueDate.getTime();
      } else if (sortBy === 'status') {
        comparison = a.status.localeCompare(b.status);
      } else if (sortBy === 'location') {
        comparison = a.locationName.localeCompare(b.locationName);
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    setFilteredTasks(filtered);
  };

  const handleCompleteTask = (task: TaskWithDetails) => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to complete tasks.', 'error');
      navigate('/subscription');
      return;
    }

    setSelectedTask(task);
    setShowCompleteModal(true);
  };

  const handleViewTask = (task: TaskWithDetails) => {
    setSelectedTask(task);
    setShowViewModal(true);
  };

  const handleOpenTaskConversation = async (task: TaskWithDetails) => {
    if (!user?.id || !currentOrganization?.id) {
      showNotification('You must be logged in to open task conversations', 'error');
      return;
    }

    try {
      showNotification('Opening task conversation...', 'info');
      
      const { messagingIntegrationService } = await import('@/services/messagingIntegrationService');
      
      // Get or create task conversation
      const result = await messagingIntegrationService.getOrCreateTaskConversation(
        task.id,
        task.locationId,
        task.templateName,
        currentOrganization.id,
        user.id,
        task.ownerRole
      );

      if (!result.success || !result.data) {
        showNotification(result.error || 'Failed to open task conversation', 'error');
        return;
      }

      // Navigate to messages page with the conversation ID
      // We'll use URL parameters to open the conversation
      window.open(`/messages?conversation=${result.data.id}`, '_blank');
    } catch (error: any) {
      console.error('Error opening task conversation:', error);
      showNotification('Failed to open task conversation', 'error');
    }
  };

  const handleTaskCompletion = async (completionData: TaskCompletionData) => {
    if (!selectedTask || !user) return;

    console.log('🔄 Task completed:', completionData);
    
    console.log('🔍 Debug user object:', user);
    console.log('🔍 Debug user.id:', user?.id);
    console.log('🔍 Debug selectedTask.id:', selectedTask.id);
    
    try {
      // Mark task as pending_review in the tasks table
      const result = await completeTask(selectedTask.id, user.id);
      if (!result.success) {
        console.error('Failed to mark task as pending review:', result.error);
        showNotification('Failed to mark task as pending review', 'error');
        return;
      }
      
      console.log('✅ Task marked as pending review in tasks table');
      
      // Reload tasks to get the latest status from database
      await loadTasks();

      showNotification('Task submitted for review!', 'success');
    } catch (error) {
      console.error('Error completing task:', error);
      showNotification('Failed to complete task', 'error');
    }
  };

  const handleReviewTask = (task: TaskWithDetails) => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to review tasks.', 'error');
      navigate('/subscription');
      return;
    }

    if (!user || !isAdminOrManager()) {
      showNotification('Only admins and managers can review tasks', 'error');
      return;
    }

    if (task.status !== 'pending_review') {
      showNotification('Task is not pending review', 'error');
      return;
    }

    setSelectedTask(task);
    setShowReviewModal(true);
  };

  const handleReviewComplete = async () => {
    await loadTasks();
  };

  const formatDueDate = (date: Date) => {
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return `${Math.abs(diffDays)} days overdue`;
    } else if (diffDays === 0) {
      return 'Due today';
    } else if (diffDays === 1) {
      return 'Due tomorrow';
    } else {
      return `Due in ${diffDays} days`;
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      case 'pending_review':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRiskBadgeClass = (riskLevel: string) => {
    switch (riskLevel) {
      case 'High':
        return 'bg-red-100 text-red-700';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'Low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Check if user is on free (communicate) plan
  const normalizedPlan = subscription?.plan ? normalizeSubscriptionPlan(subscription.plan) : null;
  const isFreePlan = !subscription || !subscription.plan || normalizedPlan === 'communicate';
  const isOperatePlan = normalizedPlan === 'operate';
  const isSubscriptionCancelled = subscription?.status === 'canceled';

  return (
    <div className="p-4 max-w-[95vw] mx-auto space-y-6">
      {/* Free Plan Upgrade Banner */}
      {isFreePlan && !isSubscriptionCancelled && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Sparkles className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Upgrade to Unlock Task Management</h3>
              <p className="text-sm text-blue-800 mb-4">
                You're currently on the <strong>Communicate (Free)</strong> plan, which includes team messaging. To create and manage tasks, upgrade to <strong>Operate</strong> or <strong>Comply</strong>. With these plans, you can add custom tasks and compliance tasks will be automatically generated from your policies.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/subscription')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Upgrade Plan
                </button>
                <button
                  onClick={() => navigate('/subscription')}
                  className="px-4 py-2 bg-white text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium"
                >
                  View Plans
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Operate Plan Educational Banner */}
      {isOperatePlan && !isSubscriptionCancelled && (
        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Info className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-green-900 mb-2">Operate Plan - Custom Tasks Only</h3>
              <p className="text-sm text-green-800 mb-4">
                You're currently on the <strong>Operate</strong> plan. This page will show custom tasks created from forms, checklists, or using the "Add Task" button. However, <strong>compliance tasks will not be automatically generated</strong> from your compliance policies. Upgrade to the <strong>Comply</strong> plan to enable automatic compliance task generation.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/subscription')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Upgrade to Comply Plan
                </button>
                <button
                  onClick={() => navigate('/subscription')}
                  className="px-4 py-2 bg-white text-green-600 border border-green-600 rounded-lg hover:bg-green-50 transition-colors text-sm font-medium"
                >
                  View Plans
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-semibold text-gray-800">Tasks</h2>
              <ContextualHelp category="Tasks" tooltip="Help with Tasks" />
            </div>
            <p className="text-sm text-gray-600 mt-1">Track and complete compliance requirements and tasks</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                if (isFreePlan && !isSubscriptionCancelled) {
                  setShowUpgradeModal(true);
                  return;
                }
                setShowAddTaskPanel(true);
              }}
              className="btn-primary px-4 py-2 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Task
            </button>
          <button
            onClick={loadTasks}
            disabled={isLoading}
              className="btn-secondary px-4 py-2 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          {assetIdFromUrl && (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900">
              <span>Showing tasks for this asset.</span>
              <button
                type="button"
                onClick={() => {
                  setSearchParams(prev => {
                    const next = new URLSearchParams(prev);
                    next.delete('assetId');
                    return next;
                  });
                }}
                className="text-amber-700 hover:text-amber-900 underline"
              >
                Show all tasks
              </button>
            </div>
          )}
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div className="flex-1 min-w-[200px] relative">
              <Package className="absolute left-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Filter by asset name..."
                value={assetNameSearch}
                onChange={(e) => setAssetNameSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* My Tasks Filter - Only show for managers/admins, not for system role "user" */}
            {!isSystemUser && (
              <label className="flex items-center gap-2 cursor-pointer shrink-0 py-1.5">
                <input
                  type="checkbox"
                  checked={filterMyTasks}
                  onChange={(e) => setFilterMyTasks(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="text-sm text-gray-700 whitespace-nowrap">My Tasks Only</span>
              </label>
            )}

            {/* Status Filter */}
            <div className="flex items-center gap-2 shrink-0">
              <Filter className="w-4 h-4 text-gray-500 shrink-0" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white min-w-[180px]"
              >
                <option value="active">Active (Due Soon + Overdue + Review)</option>
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="overdue">Overdue</option>
                <option value="pending_review">Pending Review</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            {/* Risk Level Filter */}
            <select
              value={filterRiskLevel}
              onChange={(e) => setFilterRiskLevel(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white min-w-[140px] shrink-0"
            >
              <option value="all">All Risk Levels</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>

            {/* Location Filter */}
            {hasActiveFilters ? (
              <div className="flex items-center gap-2 px-3 py-2 text-sm bg-primary-50 border border-primary-300 rounded-md shrink-0 max-w-[220px]">
                <span className="text-primary-900 font-medium truncate">
                  Filtered: {selectedLocationIds.length} location{selectedLocationIds.length !== 1 ? 's' : ''}
                </span>
                <span className="text-primary-700 text-xs truncate">
                  ({availableLocations
                    .filter(loc => selectedLocationIds.includes(loc.id!))
                    .map(loc => loc.name)
                    .slice(0, 2)
                    .join(', ')}
                  {selectedLocationIds.length > 2 && ` +${selectedLocationIds.length - 2}`})
                </span>
              </div>
            ) : (
            <select
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white min-w-[140px] shrink-0"
              title={`Found ${Array.from(new Set(tasks.map(t => t.locationName))).length} locations with tasks`}
            >
              <option value="all">All Locations ({Array.from(new Set(tasks.map(t => t.locationName))).length})</option>
              {Array.from(new Set(tasks.map(t => t.locationName))).sort().map(location => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
            )}

            {/* State Filter */}
            <select
              value={filterState}
              onChange={(e) => setFilterState(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white min-w-[120px] shrink-0"
            >
              <option value="all">All States</option>
              {Array.from(new Set(tasks.map(t => t.locationState).filter(Boolean))).sort().map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>

            {/* Category Filter - use full list (industry + custom) so newly added categories appear */}
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white min-w-[140px] shrink-0"
              title="Filter by task category"
            >
              <option value="all">All Categories</option>
              {(() => {
                const fromTasks = Array.from(new Set(tasks.map(t => (t.category || '').trim()).filter(Boolean)));
                const combined = Array.from(new Set([...taskCategories, ...fromTasks])).sort();
                return combined.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ));
              })()}
            </select>
          </div>

          <div className="text-sm text-gray-600">
            Showing {filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''} 
            {filterStatus === 'active' && ' - Active (Due Soon + Overdue + Review)'}
            {filterStatus !== 'all' && filterStatus !== 'active' && ` - ${filterStatus}`}
            {filterRiskLevel !== 'all' && ` - ${filterRiskLevel} risk`}
            {filterCategory !== 'all' && ` - ${filterCategory}`}
          </div>
        </div>

        {/* No Organization Message */}
        {!currentOrganization?.id && (
          <div className="text-center py-12 bg-yellow-50 border border-yellow-200 rounded-lg">
            <Building2 className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
            <p className="text-lg font-medium text-yellow-800">No Organization Active</p>
            <p className="text-sm mt-1 text-yellow-700">
              Please create or join an organization to view tasks
            </p>
            <button
              onClick={() => window.location.href = '/organizations'}
              className="btn-primary mt-4 px-4 py-2"
            >
              Go to Organizations
            </button>
          </div>
        )}

        {/* Tasks List */}
        {!currentOrganization?.id ? null : isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading tasks...</p>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg font-medium">No tasks found</p>
            <p className="text-sm mt-1">Try adjusting your filters or create more locations with assigned templates</p>
            <div className="mt-3 flex items-center gap-3 justify-center">
              <a href="/help/getting-started" className="text-primary-600 underline text-sm">Learn how to get started</a>
              <a href="#open-chat" className="text-primary-600 underline text-sm">Ask AI</a>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Task</th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200 cursor-pointer hover:bg-gray-100"
                    onClick={() => {
                      setSortBy('location');
                      setSortOrder(sortBy === 'location' && sortOrder === 'asc' ? 'desc' : 'asc');
                    }}
                  >
                    <div className="flex items-center gap-1">
                      Location
                      {sortBy === 'location' && (sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                      {sortBy !== 'location' && <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Category</th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200 cursor-pointer hover:bg-gray-100"
                    onClick={() => {
                      setSortBy('dueDate');
                      setSortOrder(sortBy === 'dueDate' && sortOrder === 'asc' ? 'desc' : 'asc');
                    }}
                  >
                    <div className="flex items-center gap-1">
                      Due Date
                      {sortBy === 'dueDate' && (sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                      {sortBy !== 'dueDate' && <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Risk</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Owner</th>
                  <th 
                    className="px-4 py-3 text-center text-xs font-semibold text-gray-700 border border-gray-200 cursor-pointer hover:bg-gray-100"
                    onClick={() => {
                      setSortBy('status');
                      setSortOrder(sortBy === 'status' && sortOrder === 'asc' ? 'desc' : 'asc');
                    }}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Status
                      {sortBy === 'status' && (sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                      {sortBy !== 'status' && <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 border border-gray-200">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map((task, index) => {
                  return (
                  <tr key={task.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}>
                    <td className="px-4 py-3 border border-gray-200">
                      <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-sm text-gray-900 flex items-center gap-2">
                        {task.assetName ? `${task.assetName} - ` : ''}{task.templateName}
                        {/* Reference Image Thumbnails */}
                        {task.referenceImageUrls && task.referenceImageUrls.length > 0 && (
                          <div className="flex items-center gap-1">
                            {task.referenceImageUrls.slice(0, 3).map((imageUrl, imgIndex) => {
                              // Find metadata for this image to get the note
                              const imageMetadata = task.referenceImageMetadata?.find(meta => meta.url === imageUrl);
                              const note = imageMetadata?.note;
                              const tooltipText = note 
                                ? `Image ${imgIndex + 1} of ${task.referenceImageUrls?.length || 0}\nNote: ${note}`
                                : `Click to view image ${imgIndex + 1} of ${task.referenceImageUrls?.length || 0}`;
                              
                              return (
                                <img
                                  key={imgIndex}
                                  src={imageUrl}
                                  alt={`Reference ${imgIndex + 1}`}
                                  className="w-8 h-8 object-cover rounded border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const imageUrls = task.referenceImageUrls || [];
                                    setViewingImageUrls(imageUrls);
                                    setViewingImageIndex(imgIndex);
                                    setViewingImageUrl(imageUrl);
                                    setViewingTask(task);
                                  }}
                                  title={tooltipText}
                                />
                              );
                            })}
                            {(task.referenceImageUrls?.length || 0) > 3 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const imageUrls = task.referenceImageUrls || [];
                                  if (imageUrls.length > 0) {
                                    setViewingImageUrls(imageUrls);
                                    setViewingImageIndex(0);
                                    setViewingImageUrl(imageUrls[0]);
                                    setViewingTask(task);
                                  }
                                }}
                                className="w-8 h-8 flex items-center justify-center rounded border border-gray-300 bg-gray-100 text-xs font-medium text-gray-700 hover:bg-gray-200 transition-colors"
                                title={`View all ${task.referenceImageUrls?.length || 0} images`}
                              >
                                +{(task.referenceImageUrls?.length || 0) - 3}
                              </button>
                            )}
                          </div>
                        )}
                        </div>
                        {!task.templateId || task.templateId === null || task.templateId === '' ? (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300">
                            Custom
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                            Compliance
                          </span>
                        )}
                        {task.rejectedBy && task.rejectionReason && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setRejectedTask(task);
                              setShowRejectionModal(true);
                            }}
                            className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-300 flex items-center gap-1 hover:bg-red-200 hover:border-red-400 transition-colors cursor-pointer"
                          >
                            <XCircle className="w-3 h-3" />
                            Previously Rejected
                          </button>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{task.passFailCriteria}</div>
                    </td>
                    <td className="px-4 py-3 border border-gray-200">
                      <div className="flex items-center gap-1 text-sm text-gray-700">
                        <MapPin className="w-3 h-3" />
                        {task.locationName}
                      </div>
                    </td>
                    <td className="px-4 py-3 border border-gray-200">
                      <span className="text-sm text-gray-700">{task.category}</span>
                    </td>
                    <td className="px-4 py-3 border border-gray-200">
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="w-3 h-3 text-gray-500" />
                        <div>
                          <div className="text-gray-900">{formatDueDate(task.dueDate)}</div>
                          <div className="text-xs text-gray-500">{task.dueDate.toLocaleDateString()}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 border border-gray-200">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskBadgeClass(task.riskLevel)}`}>
                        {task.riskLevel}
                      </span>
                    </td>
                    <td className="px-4 py-3 border border-gray-200">
                      <div className="space-y-1">
                        {(() => {
                          // Priority 1: Use assignedUserIds if available
                          // Handle null, undefined, and ensure it's an array
                          const assignedUserIds = (task.assignedUserIds && Array.isArray(task.assignedUserIds) && task.assignedUserIds.length > 0) 
                            ? task.assignedUserIds 
                            : (task.assignedUserId ? [task.assignedUserId] : []);
                          let responsibleUsers: Array<{ id: string; email: string; name?: string }> = [];
                          
                          if (assignedUserIds.length > 0) {
                            // Use explicitly assigned users
                            responsibleUsers = allUsers.filter(user => assignedUserIds.includes(user.id));
                            // When we have explicit assignments, show "Assigned Users" instead of owner role
                            return (
                              <>
                                <div className="font-medium text-gray-900">
                                  Assigned Users
                                </div>
                                {responsibleUsers.length === 0 ? (
                                  <div className="text-xs text-gray-500 italic">
                                    No users assigned
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-600 space-y-0.5">
                                    {responsibleUsers.map(user => (
                                      <div key={user.id} className="truncate max-w-[200px]" title={user.email}>
                                        {user.name || user.email}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </>
                            );
                          } else {
                            // Fallback to ownerRole-based logic (backward compatibility)
                            const taskOwnerRole = task.templateSnapshot?.owner_role || task.ownerRole;
                            
                            // Find users who have this owner role AND access to the task's location
                            if (taskOwnerRole && currentOrganization?.id) {
                              const organizationId = currentOrganization.id;
                              responsibleUsers = allUsers.filter(user => {
                                // Get organization-specific owner roles for this user
                                const userOwnerRoles = getUserOwnerRolesForOrganization(user, organizationId);
                                const hasOwnerRole = userOwnerRoles.includes(taskOwnerRole);
                                
                                // Also check if user has access to this task's location
                                const userLocationIds = user.locationIds || [];
                                const hasLocationAccess = task.locationId && userLocationIds.includes(task.locationId);
                                
                                // User must have both the owner role AND location access
                                return hasOwnerRole && hasLocationAccess;
                              });
                            }
                            
                            return (
                              <>
                                <div className="font-medium text-gray-900">
                                  {taskOwnerRole || 'Not assigned'}
                                </div>
                                {responsibleUsers.length === 0 ? (
                                  <div className="text-xs text-gray-500 italic">
                                    No users assigned
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-600 space-y-0.5">
                                    {responsibleUsers.map(user => (
                                      <div key={user.id} className="truncate max-w-[200px]" title={user.email}>
                                        {user.name || user.email}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </>
                            );
                          }
                        })()}
                      </div>
                    </td>
                    <td className="px-4 py-3 border border-gray-200">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(task.status)}`}>
                        {task.status === 'overdue' && <AlertTriangle className="inline w-3 h-3 mr-1" />}
                        {task.status === 'pending_review' && <CheckCircle2 className="inline w-3 h-3 mr-1" />}
                        {task.status === 'pending_review' 
                          ? 'Pending Review'
                          : task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 border border-gray-200">
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        {/* Message Button - Always visible */}
                        <button
                          onClick={() => handleOpenTaskConversation(task)}
                          className={`text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors ${
                            taskConversations.has(task.id)
                              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          title={taskConversations.has(task.id) ? 'Open task discussion (conversation exists)' : 'Open task discussion'}
                        >
                          <MessageSquare className="w-3 h-3" />
                        </button>
                        
                        {/* Other action buttons */}
                        {task.status === 'pending_review' && isAdminOrManager() ? (
                          <button
                            onClick={() => handleReviewTask(task)}
                            className="bg-orange-600 text-white hover:bg-orange-700 text-xs px-3 py-1 rounded flex items-center gap-1"
                            title="Review and approve task"
                          >
                            <CheckCircle2 className="w-3 h-3" />
                            Review
                          </button>
                        ) : task.status === 'pending_review' && !isAdminOrManager() ? (
                          <button
                            onClick={() => handleViewTask(task)}
                            className="btn-secondary text-xs px-3 py-1 flex items-center gap-1"
                            title="View Task"
                          >
                            <Eye className="w-3 h-3" />
                            View
                          </button>
                        ) : task.status === 'completed' ? (
                          <button
                            onClick={() => handleViewTask(task)}
                            className="btn-secondary text-xs px-3 py-1 flex items-center gap-1"
                            title="View Completed Task"
                          >
                            <Eye className="w-3 h-3" />
                            View
                          </button>
                        ) : (
                          <button
                            onClick={() => handleCompleteTask(task)}
                            className="btn-primary text-xs px-3 py-1 flex items-center gap-1"
                            title="Complete Task"
                          >
                            <Check className="w-3 h-3" />
                            Complete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Complete Task Modal */}
      {showCompleteModal && selectedTask && (
        <CompleteTaskModal
          task={selectedTask}
          onComplete={handleTaskCompletion}
          onClose={() => {
            setShowCompleteModal(false);
            setSelectedTask(null);
          }}
        />
      )}

      {/* View Task Modal */}
      {showViewModal && selectedTask && (
        <ViewTaskModal
          task={selectedTask}
          onClose={() => {
            setShowViewModal(false);
            setSelectedTask(null);
          }}
        />
      )}

      {/* Review Task Modal */}
      {showReviewModal && selectedTask && (
        <ReviewTaskModal
          task={selectedTask}
          onClose={() => {
            setShowReviewModal(false);
            setSelectedTask(null);
          }}
          onReview={handleReviewComplete}
        />
      )}

      {/* Rejection Details Modal */}
      {showRejectionModal && rejectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[130] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
            <div className="bg-red-50 border-b border-red-200 px-6 py-4 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600" />
                <h3 className="text-lg font-semibold text-red-900">Task Rejection Details</h3>
              </div>
              <button
                onClick={() => {
                  setShowRejectionModal(false);
                  setRejectedTask(null);
                }}
                className="text-red-600 hover:text-red-800 hover:bg-red-100 rounded-full p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-4">
                <div>
                  <span className="text-xs font-medium text-gray-500">Task Name</span>
                  <p className="text-sm font-medium text-gray-900 mt-1">{rejectedTask.templateName}</p>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500">Rejected By</span>
                  <p className="text-sm text-gray-900 mt-1">{rejectedTask.rejectedBy}</p>
                </div>
                {rejectedTask.rejectedAt && (
                  <div>
                    <span className="text-xs font-medium text-gray-500">Rejected On</span>
                    <p className="text-sm text-gray-900 mt-1">
                      {(() => {
                        try {
                          const rejectedDate = rejectedTask.rejectedAt?.toDate ? rejectedTask.rejectedAt.toDate() : 
                                              (rejectedTask.rejectedAt instanceof Date ? rejectedTask.rejectedAt :
                                              (typeof rejectedTask.rejectedAt === 'number' ? new Date(rejectedTask.rejectedAt) : new Date(rejectedTask.rejectedAt)));
                          return rejectedDate.toLocaleString();
                        } catch (error) {
                          return 'Invalid Date';
                        }
                      })()}
                    </p>
                  </div>
                )}
                <div>
                  <span className="text-xs font-medium text-gray-500">Reason for Rejection</span>
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-900 whitespace-pre-wrap">{rejectedTask.rejectionReason}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShowRejectionModal(false);
                  setRejectedTask(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Task Panel */}
      {showAddTaskPanel && !isFreePlan && (
        <AddTaskPanel
          onClose={() => setShowAddTaskPanel(false)}
          onTaskCreated={(taskIds) => {
            loadTasks();
          }}
        />
      )}

      {/* Full-Screen Image Viewer */}
      {viewingImageUrl && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-[200] flex items-center justify-center"
          onClick={() => {
            setViewingImageUrl(null);
            setViewingImageUrls([]);
            setViewingImageIndex(0);
            setViewingTask(null);
          }}
        >
          <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center p-4">
            {/* Close Button */}
            <button
              onClick={() => {
                setViewingImageUrl(null);
                setViewingImageUrls([]);
                setViewingImageIndex(0);
                setViewingTask(null);
              }}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full flex items-center justify-center text-white transition-colors"
              title="Close (Esc)"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Previous Button */}
            {viewingImageUrls.length > 1 && viewingImageIndex > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = viewingImageIndex - 1;
                  setViewingImageIndex(newIndex);
                  setViewingImageUrl(viewingImageUrls[newIndex]);
                }}
                className="absolute left-4 z-10 w-12 h-12 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full flex items-center justify-center text-white transition-colors"
                title="Previous image (←)"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}

            {/* Next Button */}
            {viewingImageUrls.length > 1 && viewingImageIndex < viewingImageUrls.length - 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = viewingImageIndex + 1;
                  setViewingImageIndex(newIndex);
                  setViewingImageUrl(viewingImageUrls[newIndex]);
                }}
                className="absolute right-4 z-10 w-12 h-12 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full flex items-center justify-center text-white transition-colors"
                title="Next image (→)"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}

            {/* Image */}
            <img
              src={viewingImageUrl}
              alt={`Reference image ${viewingImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Image Counter and Note */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2">
              {viewingImageUrls.length > 1 && (
                <div className="bg-black bg-opacity-50 text-white px-4 py-2 rounded-full text-sm">
                  {viewingImageIndex + 1} / {viewingImageUrls.length}
                </div>
              )}
              {/* Show note if available */}
              {viewingTask?.referenceImageMetadata && viewingImageUrl && (() => {
                const currentImageMetadata = viewingTask.referenceImageMetadata.find(
                  meta => meta.url === viewingImageUrl
                );
                if (currentImageMetadata?.note) {
                  return (
                    <div className="bg-black bg-opacity-70 text-white px-4 py-2 rounded-lg text-sm max-w-md text-center">
                      <div className="font-medium mb-1">Note:</div>
                      <div>{currentImageMetadata.note}</div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>

        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          onClick={() => setShowUpgradeModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Upgrade Required</h3>
                <p className="text-sm text-gray-600">Task Creation</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-700 mb-6">
              Creating tasks requires an <strong>Operate</strong> or <strong>Comply</strong> plan. Upgrade now to unlock task creation and management features!
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  navigate('/subscription');
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskManager;
